import React, { useState } from 'react';
import { getActivityTypeIcon } from '../utils/iconUtils';

interface ActivityItemProps {
  activity: {
    activity_id: string;
    title: string;
    type: string;
    description?: string;
  };
  isEditing?: boolean;
  isSelected?: boolean;
  onEdit: (activityId: string) => void;
  onSave?: (activityId: string, newTitle: string) => void;
  onCancel?: () => void;
  onDelete: (activityId: string) => void;
  onSelect: (activityId: string) => void;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ 
  activity,
  isEditing = false,
  isSelected = false,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onSelect
}) => {
  const [editedTitle, setEditedTitle] = useState(activity.title);

  const handleSave = () => {
    if (onSave && editedTitle.trim()) {
      onSave(activity.activity_id, editedTitle);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape' && onCancel) {
      onCancel();
    }
  };

  const handleActivityClick = (e: React.MouseEvent) => {
    // Only trigger selection if not clicking on the edit/delete buttons or input field
    if ((e.target as HTMLElement).tagName !== 'BUTTON' && 
        (e.target as HTMLElement).tagName !== 'svg' && 
        (e.target as HTMLElement).tagName !== 'path' && 
        (e.target as HTMLElement).tagName !== 'INPUT') {
      onSelect(activity.activity_id);
    }
  };

  return (
    <div 
      className={`flex items-center justify-between p-2 rounded text-sm transition-colors cursor-pointer ${
        isSelected 
          ? 'bg-blue-50 border border-blue-200' 
          : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
      }`}
      onClick={handleActivityClick}
    >
      <div className="flex items-center flex-grow">
        <div 
          className={`mr-2 flex-shrink-0 cursor-pointer hover:bg-gray-200 p-1 rounded transition-colors ${
            isSelected ? 'bg-blue-100' : ''
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(activity.activity_id);
          }}
          title="Click to view activity details"
        >
          {getActivityTypeIcon(activity.type)}
        </div>
        <div className="flex-grow min-h-[20px] flex items-center relative">
          {isEditing ? (
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              className="absolute inset-0 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ 
                margin: 0, 
                padding: '0 2px', 
                height: '20px',
                lineHeight: '20px',
                boxSizing: 'border-box'
              }}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span 
              className={`${isSelected ? 'text-blue-700 font-medium' : 'text-gray-900'} cursor-text inline-block w-full truncate overflow-hidden whitespace-nowrap max-w-[180px]`}
              style={{ 
                padding: '1px 2px',
                lineHeight: '18px',
                height: '20px',
                boxSizing: 'border-box'
              }}
              title={activity.title}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(activity.activity_id);
              }}
            >
              {activity.title}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
        <button 
          className="p-1 text-gray-400 hover:text-blue-600"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(activity.activity_id);
          }}
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button 
          className="p-1 text-gray-400 hover:text-red-600"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(activity.activity_id);
          }}
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ActivityItem; 